import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { toNum } from '@shared/helpers/math.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import { getDatiBilancioGroup, getDatiBilancioBisControl } from './helpers/dati-bilancio-bis-form-validators.helper';

export const datiBilancioBisDatiBilancioRules: FormGroupRules = {
    my_TipoBilancioAbbr1: (form: FormGroup) => {
        const datiBilancioGroup = getDatiBilancioGroup(form);
        const my_TipoBilancioAbbr1Control = getControl(datiBilancioGroup, 'my_TipoBilancioAbbr1');
        const my_DataApprovazione1: string = getControl(datiBilancioGroup, 'my_DataApprovazione1')?.value;
        const showError = isNilOrEmpty(my_TipoBilancioAbbr1Control?.value) && !isNilOrEmpty(my_DataApprovazione1);
        const errors = my_TipoBilancioAbbr1Control.errors ? my_TipoBilancioAbbr1Control.errors : {};

        addRemoveError(showError, errors, 'my_TipoBilancioAbbr1', {
            status: REQUIRED_STATUS,
        });

        my_TipoBilancioAbbr1Control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DataApprovazione1: (form: FormGroup) => {
        const datiBilancioGroup = getDatiBilancioGroup(form);
        const my_DataApprovazione1Control = getControl(datiBilancioGroup, 'my_DataApprovazione1');
        const my_TipoSchemaBil1: string = getControl(datiBilancioGroup, 'my_TipoSchemaBil1')?.value;
        const errors = my_DataApprovazione1Control.errors ? my_DataApprovazione1Control.errors : {};
        const dateValue = my_DataApprovazione1Control?.value;
        const isEmptyDate = isNilOrEmpty(dateValue) || dateValue === 0 || dateValue === '0';
        const showError = isEmptyDate && !isNilOrEmpty(my_TipoSchemaBil1);

        addRemoveError(showError, errors, 'my_DataApprovazione1', {
            status: REQUIRED_STATUS,
        });

        my_DataApprovazione1Control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_TipoSchemaBil1: (form: FormGroup) => {
        const datiBilancioGroup = getDatiBilancioGroup(form);
        const my_TipoSchemaBil1Control = getControl(datiBilancioGroup, 'my_TipoSchemaBil1');
        const my_DataApprovazione1: string = getControl(datiBilancioGroup, 'my_DataApprovazione1')?.value;
        const showError = isNilOrEmpty(my_TipoSchemaBil1Control?.value) && !isNilOrEmpty(my_DataApprovazione1);
        const errors = my_TipoSchemaBil1Control.errors ? my_TipoSchemaBil1Control.errors : {};

        addRemoveError(showError, errors, 'my_TipoSchemaBil1', {
            status: "Occorre specificare il tipo di schema di bilancio utilizzato per l'anno 1 se si specifica la data di approvazione",
        });

        my_TipoSchemaBil1Control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_NumBilanciInExcel: (form: FormGroup) => {
        const bilancioGroup = getGroup(form, formPaths.bilancio);
        const datiBilancioGroup = getDatiBilancioGroup(form);
        const my_NumBilanciInExcelControl = getControl(bilancioGroup, 'my_NumBilanciInExcel');
        const my_NumBilanciInExcel: string = my_NumBilanciInExcelControl?.value;
        const my_numBilanciNelDBCentrale = toNum(getControl(form, 'my_numBilanciNelDBCentrale')?.value);
        const my_TipoBilancioAbbr1: string = getControl(datiBilancioGroup, 'my_TipoBilancioAbbr1')?.value;
        const my_DataApprovazione1: string = getControl(datiBilancioGroup, 'my_DataApprovazione1')?.value;
        const my_TipoSchemaBil1: string = getControl(datiBilancioGroup, 'my_TipoSchemaBil1')?.value;
        const my_BilAnno2: string = getControl(datiBilancioGroup, 'my_BilAnno2')?.value;
        const errors = my_NumBilanciInExcelControl.errors ? my_NumBilanciInExcelControl.errors : {};
        const mancaBilancioAnno1Error =
            my_NumBilanciInExcel === '2' &&
            isNilOrEmpty(my_TipoSchemaBil1) &&
            isNilOrEmpty(my_DataApprovazione1) &&
            isNilOrEmpty(my_TipoBilancioAbbr1);

        addRemoveError(isNilOrEmpty(my_NumBilanciInExcel), errors, 'my_NumBilanciInExcel', {
            status: 'Attenzione: indicare il numero di bilanci da inserire',
        });

        addRemoveError(my_NumBilanciInExcel === '0' && my_numBilanciNelDBCentrale >= 1, errors, 'my_NumBilanciInExcel', {
            status: 'Attenzione: dal sistema centrale risulta depositato almeno 1 bilancio',
        });

        addRemoveError(my_NumBilanciInExcel === '1' && my_numBilanciNelDBCentrale >= 2, errors, 'my_NumBilanciInExcel', {
            status: 'Attenzione: dal sistema centrale risultano depositati almeno 2 bilanci',
        });

        addRemoveError(
            my_NumBilanciInExcel === '1' && (!isNilOrEmpty(my_DataApprovazione1) || my_TipoBilancioAbbr1 != '0'),
            errors,
            'my_NumBilanciInExcel',
            {
                status: 'Attenzione: nel file ci sono informazioni per il primo bilancio',
            }
        );

        addRemoveError(mancaBilancioAnno1Error, errors, 'my_NumBilanciInExcel', {
            status: 'Attenzione: manca il bilancio del primo anno',
        });

        addRemoveError(my_NumBilanciInExcel != '0' && my_BilAnno2 === '1900', errors, 'my_NumBilanciInExcel', {
            status: 'Attenzione: anno del bilancio incoerente',
        });

        my_NumBilanciInExcelControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CtrlDatiAggiuntiviBilBreve: (form: FormGroup) => {
        const my_CtrlDatiAggiuntiviBilBreveControl = getDatiBilancioBisControl(form, 'my_CtrlDatiAggiuntiviBilBreve');
        const showError = my_CtrlDatiAggiuntiviBilBreveControl.value != 'OK';
        const errors = my_CtrlDatiAggiuntiviBilBreveControl.errors ? my_CtrlDatiAggiuntiviBilBreveControl.errors : {};

        addRemoveError(showError, errors, 'my_CtrlDatiAggiuntiviBilBreve', {
            status: 'Controllare i dati aggiuntivi al bilancio breve',
        });

        my_CtrlDatiAggiuntiviBilBreveControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};
